import { useState, useEffect, useCallback } from 'react';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import Link from '@mui/material/Link';
import makeStyles from '@mui/styles/makeStyles';
import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
} from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { useDebounce } from 'react-use';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { Alert } from '@backstage/ui';
import { scaffolderGithubEmuTranslationRef } from '../../translation';
import { CommonStatusAlert } from '../CommonStatusAlert';

/**
 * User data structure returned from the backend API
 */
interface AzureUser {
  id: string;
  displayName: string;
  mail: string;
}

const useStyles = makeStyles(theme => ({
  container: {
    width: '100%',
  },
  option: {
    display: 'flex',
    flexDirection: 'column',
  },
  optionName: {
    fontWeight: 500,
  },
  optionEmail: {
    fontSize: '0.85em',
    color: theme.palette.text.secondary,
  },
}));

/**
 * Schema for the AzureTenantUserPicker field extension
 */
export interface AzureTenantUserPickerSchema {
  title?: string;
  description?: string;
}

/**
 * UI Schema options for the AzureTenantUserPicker
 */
interface AzureTenantUserPickerUiSchema {
  'ui:options'?: {
    /** Azure Entra group object ID whose members are excluded from results */
    groupId?: string;
    /** Placeholder text for the input field */
    placeholder?: string;
    /** Minimum characters before search is triggered */
    minSearchLength?: number;
    /** Debounce delay in milliseconds */
    debounceMs?: number;
    /** Whether to return the full user object or just the email */
    returnFullObject?: boolean;
    /** URL to onboarding page when user is not in the Azure tenant */
    onboardingUrl?: string;
    /** URL for pending users to accept their Azure tenant invitation */
    pendingAcceptanceUrl?: string;
  };
}

/**
 * AzureTenantUserPicker - A scaffolder field extension for selecting Azure AD users
 *
 * This component provides an autocomplete field that searches for users
 * in your Azure AD tenant and allows selection for use in scaffolder templates.
 */
export const AzureTenantUserPicker = (
  props: FieldExtensionComponentProps<string, AzureTenantUserPickerSchema>,
) => {
  const { onChange, rawErrors, required, formData, uiSchema, schema } = props;

  const classes = useStyles();
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const { t } = useTranslationRef(scaffolderGithubEmuTranslationRef);

  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<AzureUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AzureUser | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [userStatus, setUserStatus] = useState<{
    status:
      | 'already_onboarded'
      | 'pending_invitation'
      | 'not_in_tenant'
      | 'available';
    displayName?: string;
    mail?: string;
  } | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  // Get UI options with defaults
  const uiOptions =
    (uiSchema as AzureTenantUserPickerUiSchema)?.['ui:options'] ?? {};
  // const placeholder = uiOptions.placeholder ?? 'Search for a user by email...';
  const groupId = uiOptions.groupId;
  const minSearchLength = uiOptions.minSearchLength ?? 2;
  const debounceMs = uiOptions.debounceMs ?? 300;
  const returnFullObject = uiOptions.returnFullObject ?? false;
  const onboardingUrl = uiOptions.onboardingUrl;
  const pendingAcceptanceUrl =
    uiOptions.pendingAcceptanceUrl ?? 'https://myapplications.microsoft.com';

  // Debounce the search input
  useDebounce(
    () => {
      setDebouncedSearch(inputValue);
    },
    debounceMs,
    [inputValue],
  );

  // Fetch users when debounced search changes
  const fetchUsers = useCallback(async () => {
    if (debouncedSearch.length < minSearchLength) {
      setOptions([]);
      setUserStatus(null);
      return;
    }

    setLoading(true);
    setUserStatus(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('githubemu');
      const params = new URLSearchParams({ search: debouncedSearch });
      if (groupId) params.set('groupId', groupId);
      const response = await fetchApi.fetch(
        `${baseUrl}/azure-users?${params.toString()}`,
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.status}`);
      }

      const users: AzureUser[] = await response.json();
      setOptions(users);

      // When no selectable users returned and input looks like a full email,
      // check why the user doesn't appear
      if (
        users.length === 0 &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(debouncedSearch)
      ) {
        setCheckingStatus(true);
        try {
          const statusParams = new URLSearchParams({ email: debouncedSearch });
          if (groupId) statusParams.set('groupId', groupId);
          const statusRes = await fetchApi.fetch(
            `${baseUrl}/azure-user-status?${statusParams.toString()}`,
          );
          if (statusRes.ok) {
            const statusData = await statusRes.json();
            setUserStatus(statusData);
          }
        } catch {
          // Status check is best-effort
        } finally {
          setCheckingStatus(false);
        }
      }
    } catch (error) {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, discoveryApi, fetchApi, groupId, minSearchLength]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Handle user selection
  const handleChange = (
    _event: React.ChangeEvent<{}>,
    value: AzureUser | null,
  ) => {
    setSelectedUser(value);

    if (value) {
      if (returnFullObject) {
        onChange(JSON.stringify(value));
      } else {
        onChange(value.mail);
      }
    } else {
      // User cleared the selection
      onChange(undefined);
      setOptions([]);
      setInputValue('');
    }
  };

  // Handle input change - only trigger search when user is typing
  const handleInputChange = (
    _event: React.SyntheticEvent,
    value: string,
    reason: string,
  ) => {
    // Only update input value and trigger search when user types
    if (reason === 'input') {
      setInputValue(value);
    } else if (reason === 'clear') {
      setInputValue('');
      setDebouncedSearch('');
      setOptions([]);
      setSelectedUser(null);
      setUserStatus(null);
    } else if (reason === 'reset') {
      // When a selection is made, update inputValue to show the selected value.
      // On blur with no selection, value is '' — preserve typed input if we have
      // a status result so the alert stays visible.
      if (value || (userStatus && userStatus.status !== 'available')) {
        setInputValue(value);
      }
    }
  };

  // Initialize selected user from formData if present
  useEffect(() => {
    if (formData && !selectedUser) {
      // If formData is a JSON string (full object mode), try to parse it
      if (returnFullObject && formData.startsWith('{')) {
        try {
          const parsed = JSON.parse(formData);
          setSelectedUser(parsed);
        } catch {
          // Ignore parse errors
        }
      } else {
        // Otherwise, create a placeholder user object
        setSelectedUser({
          id: '',
          displayName: formData,
          mail: formData,
        });
      }
    }
  }, [formData, selectedUser, returnFullObject]);

  const statusAlert = (() => {
    if (!userStatus) return null;
    if (userStatus.status === 'already_onboarded') {
      return (
        <Alert
          status="info"
          icon
          title={t('azurePicker.alreadyOnboarded.title', {
            user: userStatus.displayName
              ? `${userStatus.displayName} (${userStatus.mail})`
              : (userStatus.mail ?? ''),
          })}
        />
      );
    }
    if (userStatus.status === 'pending_invitation') {
      const userLabel = userStatus.displayName
        ? `${userStatus.displayName} (${userStatus.mail})`
        : userStatus.mail;
      return (
        <CommonStatusAlert
          pendingInvitationUrl={pendingAcceptanceUrl}
          userLabel={userLabel || ''}
        />
      );
    }
    if (userStatus.status === 'not_in_tenant') {
      return (
        <Alert
          status="danger"
          icon
          title={t('azurePicker.notInTenant.title', { email: inputValue })}
          description={
            onboardingUrl ? (
              <Link href={onboardingUrl} target="_blank" rel="noopener">
                {t('azurePicker.notInTenant.onboardingLink')}
              </Link>
            ) : undefined
          }
        />
      );
    }

    return null;
  })();

  const getNoOptionsContent = () => {
    if (inputValue.length < minSearchLength) {
      return `Type at least ${minSearchLength} characters to search`;
    }
    if (checkingStatus) {
      return 'Checking user status...';
    }
    return 'No users found';
  };

  return (
    <div className={classes.container}>
      <Autocomplete
        open={open && !statusAlert}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={selectedUser}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={options}
        loading={loading}
        filterOptions={x => x}
        getOptionLabel={option => option.displayName || option.mail}
        isOptionEqualToValue={(option, value) =>
          option.id === value.id || option.mail === value.mail
        }
        renderOption={(optionProps, option) => (
          <li {...optionProps} key={option.id || option.mail}>
            <div className={classes.option}>
              <Typography className={classes.optionName}>
                {option.displayName}
              </Typography>
              <Typography className={classes.optionEmail}>
                {option.mail}
              </Typography>
            </div>
          </li>
        )}
        renderInput={params => (
          <TextField
            {...params}
            label={schema.title || 'Search by email of the user'}
            placeholder=""
            required={required}
            error={rawErrors && rawErrors.length > 0}
            helperText={schema.description || (rawErrors && rawErrors[0])}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        noOptionsText={getNoOptionsContent()}
        fullWidth
      />
      {!loading && <div style={{ marginTop: '8px' }}>{statusAlert}</div>}
    </div>
  );
};
